/**
 * What `/cwiczenia` keeps in its address, and nothing else.
 *
 * Two parameters, and each one is there because a trainer must be able to send it to somebody:
 *
 * · **`?zakres=biblioteka|twoje`** — which of the three lists. `Wszystkie` is the default and is
 *   **never serialised**: two addresses for one screen is what that prevents, and it is the rule
 *   `pages/plan-library`'s `planScope` already follows.
 * · **`?cwiczenie=<id>`** — which movement's card is open over the list. A trainer sending a
 *   colleague a link to one exercise is the whole reason it is in the URL rather than in state.
 *
 * The search term, the facets and the SELECTION stay out. A selection follows the pointer sixty
 * times a second and would put sixty entries in the browser's history; a facet is a way of looking
 * rather than a place, and the plan library settled that for both screens.
 *
 * ── An id that is not a number degrades to the LIST ─────────────────────────────────────────
 * `?cwiczenie=abc`, `?cwiczenie=`, `?cwiczenie=-4`, `?cwiczenie=2.5` are all somebody's typo or a
 * stale link, and the honest answer is the screen without a card open rather than an error page or
 * a card describing nothing. An id that parses but names a movement the catalogue does not hold is
 * the same outcome, decided one layer up, where the catalogue is known.
 */

import type { ExerciseScope } from './exerciseScope';

export interface ExerciseLibrarySearch {
  readonly zakres?: 'biblioteka' | 'twoje';
  readonly cwiczenie?: number;
}

function parseScope(raw: unknown): 'biblioteka' | 'twoje' | undefined {
  return raw === 'biblioteka' || raw === 'twoje' ? raw : undefined;
}

function parseExerciseId(raw: unknown): number | undefined {
  // `Number('')` is 0 and `Number(null)` is 0, so the guard is a positive INTEGER rather than a
  // successful parse. Ids are row counters and start at 1.
  const parsed = Number(raw);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
}

/**
 * The route's `validateSearch`.
 *
 * A key that parses to nothing is OMITTED rather than set to `undefined`: TanStack Router
 * serialises the object back into the address, and an explicit `undefined` leaves `?zakres=`
 * standing there.
 */
export function validateExerciseLibrarySearch(
  raw: Record<string, unknown>,
): ExerciseLibrarySearch {
  const zakres = parseScope(raw.zakres);
  const cwiczenie = parseExerciseId(raw.cwiczenie);
  return {
    ...(zakres === undefined ? {} : { zakres }),
    ...(cwiczenie === undefined ? {} : { cwiczenie }),
  };
}

/** Which list this address is asking for. No parameter means all three. */
export function exerciseScopeOf(search: ExerciseLibrarySearch): ExerciseScope {
  if (search.zakres === 'biblioteka') return 'library';
  if (search.zakres === 'twoje') return 'own';
  return 'all';
}

/**
 * The address for a scope, written WHOLE rather than spread over the current one.
 *
 * Spreading would carry the key this is dropping, which is how a screen ends up with
 * `?zakres=twoje` on the list it just left. The open card is dropped with it on purpose: switching
 * list is a change of subject.
 */
export function exerciseScopeSearch(scope: ExerciseScope): ExerciseLibrarySearch {
  switch (scope) {
    case 'library':
      return { zakres: 'biblioteka' };
    case 'own':
      return { zakres: 'twoje' };
    case 'all':
      return {};
  }
}

/**
 * The address with a card open over the list, or with it closed.
 *
 * Written WHOLE for the reason above, out of the two values the screen owns — the scope it is on
 * and the movement the theatre is showing. `null` closes the card and keeps the scope, which is
 * what Escape and the close square both mean: the trainer is going back to the list they were on,
 * not to the default one.
 *
 * Which movement the trainer POINTED at stays out of the address, exactly as it did before the
 * theatre landed. A selection follows the pointer sixty times a second; a card is something they
 * opened.
 */
export function exerciseCardSearch(
  scope: ExerciseScope,
  exerciseId: number | null,
): ExerciseLibrarySearch {
  return {
    ...exerciseScopeSearch(scope),
    ...(exerciseId === null ? {} : { cwiczenie: exerciseId }),
  };
}

/** Which movement's card the address has open, or `null`. */
export function exerciseCardIdOf(search: ExerciseLibrarySearch): number | null {
  return search.cwiczenie ?? null;
}
